/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable max-lines */
import BigNumber from 'bignumber.js';
import {
  GameAccountItemDepositTabContentSourceSelector,
  Message,
} from 'components';
import { ButtonWithSpinner } from 'components/ButtonWithSpinner';
import { PercentageButtons } from 'components/PercentageButtons';
import {
  isCorrectValue,
  MessageText,
  MessageType,
  numbersOnly,
  skynityConfig,
} from 'helpers';
import {
  DepositSource,
  GameServer,
  ServerType,
  TokenData,
  VestingPoolData,
  VestingPoolVestingData,
} from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, Tab } from 'react-bootstrap';

interface GameAccountItemDepositTabContentProps {
  connectedAddress: string;
  loading: boolean;
  waitingForDeposit: boolean;
  depositAllowance: string;
  inWallet: string;
  tokenData: TokenData;
  vestingPoolsData: VestingPoolData[];
  selectedVestingPoolData: VestingPoolData;
  selectedVestingPoolVestingData: VestingPoolVestingData;
  pendingDeposit: string;
  selectedGameServer: GameServer;
  onApprove: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectVestingPool: (vestingPoolData: VestingPoolData) => void;
  onDepositFromWallet: (depositValue: string) => void;
  onDepositFromVesting: (depositValue: string) => void;
}

export const GameAccountItemDepositTabContent: React.FC<
  GameAccountItemDepositTabContentProps
  // eslint-disable-next-line complexity
> = ({
  connectedAddress,
  loading,
  waitingForDeposit,
  depositAllowance,
  inWallet,
  tokenData,
  vestingPoolsData,
  selectedVestingPoolData,
  selectedVestingPoolVestingData,
  pendingDeposit,
  selectedGameServer,
  onApprove,
  onSelectVestingPool,
  onDepositFromWallet,
  onDepositFromVesting,
}) => {
  const [depositSource, setDepositSource] = useState<DepositSource>('wallet');
  const [depositValue, setDepositValue] = useState<string>('');
  const [availableAmount, setAvailableAmount] = useState<string>('');
  const depositValueBN = new BigNumber(depositValue);
  const depositAllowanceBN = new BigNumber(depositAllowance);
  const availableSdtInWalletBN = new BigNumber(inWallet);
  const depositEnabledVestingPoolsData = useMemo(
    () =>
      vestingPoolsData?.filter(
        (vestingPoolData) => vestingPoolData.depositToGameEnabled
      ),
    [vestingPoolsData]
  );
  const isClickerAppServer = selectedGameServer?.type === ServerType.ClickerApp;

  useEffect(() => {
    if (!loading) {
      setDepositValue('0');
    }
  }, [loading]);

  useEffect(() => {
    if (depositSource === 'wallet') {
      setAvailableAmount(availableSdtInWalletBN.toString(10));
    } else {
      setAvailableAmount(
        BigNumber(selectedVestingPoolVestingData?.amountLeft).toString(10)
      );
    }
  }, [depositSource, selectedVestingPoolVestingData]);

  const inputDisabled = (): boolean => {
    if (loading) {
      return true;
    }

    if (depositSource === 'wallet') {
      return availableSdtInWalletBN.isEqualTo(0);
    } else if (depositSource === 'vesting') {
      return (
        !selectedVestingPoolVestingData ||
        +selectedVestingPoolVestingData?.amountLeft === 0
      );
    }

    return true;
  };

  const depositButtonDisabled = (): boolean => {
    let isGreaterThanAvailable;

    if (depositSource === 'wallet') {
      isGreaterThanAvailable = depositValueBN.isGreaterThan(
        availableSdtInWalletBN
      );
    } else if (depositSource === 'vesting') {
      isGreaterThanAvailable = depositValueBN.isGreaterThan(
        new BigNumber(+selectedVestingPoolVestingData?.amountLeft)
      );
    }

    return (
      inputDisabled() ||
      +depositValue == 0 ||
      +depositValue < skynityConfig.minimumDepositSdt ||
      isGreaterThanAvailable
    );
  };

  const onApplyPercentage = (percentage: number): void => {
    const value =
      depositSource === 'wallet'
        ? availableSdtInWalletBN
        : new BigNumber(selectedVestingPoolVestingData.amountLeft);

    const roundedNumber = BigNumber(
      value.times(percentage).div(100).toFixed(tokenData.decimals)
    ).toString(10);

    setDepositValue(roundedNumber);
  };

  const onSetDepositSource = (depositSource: DepositSource): void => {
    setDepositValue('0');
    setDepositSource(depositSource);
    if (depositEnabledVestingPoolsData.length == 1) {
      onSelectVestingPool(depositEnabledVestingPoolsData[0]);
    } else {
      onSelectVestingPool(undefined);
    }
  };

  const onDepositValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;

    if (isCorrectValue(value, tokenData.decimals)) {
      setDepositValue(value);
    }
  };

  const onDepositButtonClick = (): void => {
    if (depositSource === 'wallet') {
      onDepositFromWallet(depositValue);
    } else if (depositSource === 'vesting') {
      onDepositFromVesting(depositValue);
    }
  };

  const canDepositFromWallet =
    depositSource === 'wallet' &&
    depositAllowanceBN.isGreaterThan(0) &&
    depositAllowanceBN.isGreaterThan(+depositValue);
  const canDeposit = depositSource === 'vesting' || canDepositFromWallet;
  const button = canDeposit ? (
    <ButtonWithSpinner
      text="Deposit"
      classes="btn btn-primary section-action-button"
      disabled={depositButtonDisabled()}
      onClick={(): void => onDepositButtonClick()}
      loading={loading}
    />
  ) : (
    <ButtonWithSpinner
      text="Approve"
      classes="btn btn-primary section-action-button"
      onClick={onApprove}
      loading={loading}
    />
  );

  return (
    <Tab.Pane eventKey="deposit" unmountOnExit={true} mountOnEnter={true}>
      {+pendingDeposit ? (
        <Message
          messageType={
            waitingForDeposit ? MessageType.Primary : MessageType.Error
          }
        >
          <div className="d-flex align-items-center gap-3">
            {waitingForDeposit && (
              <div>
                <Spinner></Spinner>
              </div>
            )}
            <div>
              {waitingForDeposit
                ? `Please wait for the current deposit to process on the server.`
                : `You have another ongoing deposit from your wallet/vesting on this
              server - please refresh the page after a couple of minutes.`}
            </div>
          </div>
        </Message>
      ) : (
        <div className="tab-content">
          <div className="unstake-section">
            <div className="unstake-section-description">
              Deposit your SkyDust (SDT) tokens into the game:
              <ul>
                <li>From your wallet 1 SDT token into 1 SDT in-game</li>
                {isClickerAppServer ? (
                  <li>From vesting 1 SDT token into 1 vSDT in-game</li>
                ) : (
                  <li>
                    From vesting instantly converted into the rSDT (raw SkyDust)
                    in-game currency at current ratio{' '}
                    <span className="primary">
                      1:
                      {selectedGameServer.stats.deposit_ratio ??
                        selectedGameServer.deposit_ratio.base}
                    </span>{' '}
                    (for every <span className="primary">1 SDT</span> you will
                    get{' '}
                    <span className="primary">
                      {' '}
                      {selectedGameServer.stats.deposit_ratio ??
                        selectedGameServer.deposit_ratio.base}{' '}
                      rSDT
                    </span>{' '}
                    ).
                    {!!selectedGameServer.deposit_ratio.incr &&
                      !!selectedGameServer.stats.deposit_ratio && (
                        <div>
                          Ratio is rising from{' '}
                          <span className="primary">
                            1:{selectedGameServer.deposit_ratio.base}
                          </span>
                          , increasing by{' '}
                          <span className="primary">
                            {selectedGameServer.deposit_ratio.incr} rSDT{' '}
                          </span>
                          every{' '}
                          <span className="primary">
                            {selectedGameServer.deposit_ratio.interval / 60}{' '}
                          </span>
                          minutes until a maximum of{' '}
                          <span className="primary">
                            1:{selectedGameServer.deposit_ratio.max}{' '}
                          </span>
                          is reached
                        </div>
                      )}
                  </li>
                )}
              </ul>
            </div>
            <div className="unstake-section-description fw-bold">
              <Message messageType={MessageType.Primary}>
                SDT tokens can be purchased on{' '}
                <a
                  href={skynityConfig.additionalData.tokenLink}
                  target="_blank"
                  className="link primary link-external"
                  rel="noreferrer"
                >
                  Uniswap
                </a>
              </Message>
            </div>

            <div className="unstake-section-actions">
              <GameAccountItemDepositTabContentSourceSelector
                connectedAddress={connectedAddress}
                tokenData={tokenData}
                depositSource={depositSource}
                loading={loading}
                vestingPoolsData={depositEnabledVestingPoolsData}
                selectedVestingPoolData={selectedVestingPoolData}
                onSelectVestingPool={(vestingPoolData: VestingPoolData): void =>
                  onSelectVestingPool(vestingPoolData)
                }
                onSetDepositSource={(depositSource: DepositSource): void =>
                  onSetDepositSource(depositSource)
                }
              />

              {canDepositFromWallet || selectedVestingPoolData ? (
                <>
                  <div className="text-end">
                    <small>
                      {skynityConfig.minimumDepositSdt &&
                        `min. ${skynityConfig.minimumDepositSdt} SDT, `}
                      max: {availableAmount} SDT
                    </small>
                  </div>
                  <input
                    className="s-input"
                    placeholder="0.00"
                    onChange={onDepositValueChange}
                    value={depositValue}
                    onKeyPress={numbersOnly}
                    disabled={inputDisabled()}
                  />
                  <PercentageButtons
                    disabled={inputDisabled()}
                    applyPercentage={(percentage: number): void =>
                      onApplyPercentage(percentage)
                    }
                  />
                </>
              ) : (
                ''
              )}

              {depositSource === 'wallet' && !canDepositFromWallet ? (
                <div className="mb-16">{MessageText.Approve}</div>
              ) : (
                ''
              )}

              {button}
            </div>

            {/* TODO */}
            {/* {withdrawValueBN.isGreaterThan(totalTokensInGameBn) && (
            <Message
              messageType={MessageType.Error}
              descriptionText={MessageText.WithdrawValueGreaterThanAvailable}
            />
          )} */}
          </div>
        </div>
      )}
    </Tab.Pane>
  );
};
